import { useSelector } from 'react-redux';

import { selectUserAccountId } from 'user/api/selectors';

import { useFilterMembersMutation } from './api';
import { tagServiceApi } from './tagServiceApi';
import type { TagCategory, TagFilter, TagSelections } from './types';

const EMPTY_CATEGORIES: TagCategory[] = [];
const EMPTY_SELECTIONS: TagSelections = {};

const useGetTagCategoriesQuery = tagServiceApi.endpoints.getTagCategories.useQuery;
const useSetTagCategoriesMutation = tagServiceApi.endpoints.setTagCategories.useMutation;
const useGetMyTagsQuery = tagServiceApi.endpoints.getMyTags.useQuery;
const useSetMyTagsMutation = tagServiceApi.endpoints.setMyTags.useMutation;

export function useTags(communityId: number | undefined) {
  const accountId = useSelector(selectUserAccountId);

  const { data: categories, isLoading: categoriesLoading } = useGetTagCategoriesQuery(
    { communityId: communityId! },
    { skip: !communityId }
  );
  const { data: mySelections, isLoading: mySelectionsLoading } = useGetMyTagsQuery(
    { communityId: communityId!, accountId: accountId! },
    { skip: !communityId || !accountId }
  );
  const [setTagCategories] = useSetTagCategoriesMutation();
  const [setMyTagsMutation] = useSetMyTagsMutation();
  const [filterMembersMutation] = useFilterMembersMutation();

  const setCategories = async (cats: TagCategory[]): Promise<void> => {
    if (!communityId) return;
    await setTagCategories({ communityId, categories: cats }).unwrap();
  };

  const setMyTags = async (selections: TagSelections): Promise<void> => {
    if (!communityId || !accountId) return;
    await setMyTagsMutation({ communityId, accountId, selections }).unwrap();
  };

  const filterMembers = (filters: TagFilter[]) => {
    if (!communityId) return Promise.resolve([]);
    return filterMembersMutation({ communityId, filters }).unwrap();
  };

  return {
    categories: categories ?? EMPTY_CATEGORIES,
    mySelections: mySelections ?? EMPTY_SELECTIONS,
    isLoading: categoriesLoading || mySelectionsLoading,
    setCategories,
    setMyTags,
    filterMembers,
  };
}
